import { useEffect, useMemo } from "react";
import { getIn, useFormikContext } from "formik";
import { useAppSelector } from "@app/hooks";
import { useGetSOCrefQuery } from "@hooks/index";
import { selectCurrentProject } from "@features/project/projectSlice";
import GenericInputGroup from "@components/input/inputGroup/GenericInputGroup";
import { ClassicInputGroup, FieldType } from "@typedefs/modulesInterfaces";

interface ActivityDefaultSocSubmoduleProps {
	climateField: string;
	moistureField: string;
	socT2Field: string;
	disabled?: boolean;
}

const ActivityDefaultSocSubmodule = ({
	climateField,
	moistureField,
	socT2Field,
	disabled = false,
}: ActivityDefaultSocSubmoduleProps) => {
	const { values, setFieldValue } = useFormikContext<any>();
	const { project } = useAppSelector(selectCurrentProject);

	const climate = (getIn(values, climateField) as number | null | undefined) ?? null;
	const moisture = (getIn(values, moistureField) as number | null | undefined) ?? null;
	const soilType = project?.soil_type ?? null;
	const hasProjectSocT2 = project?.soc_ref_t2 !== null && project?.soc_ref_t2 !== undefined;

	const { data: socRef } = useGetSOCrefQuery(
		{ climate, moisture, soil_type: soilType },
		{
			skip: hasProjectSocT2 || !climate || !moisture || !soilType,
			refetchOnMountOrArgChange: true,
		},
	);

	const defaultSocT2 = useMemo(() => {
		if (hasProjectSocT2) return project?.soc_ref_t2 ?? null;
		return socRef?.value ?? null;
	}, [hasProjectSocT2, project?.soc_ref_t2, socRef?.value]);

	const defaultSocInputName = `${socT2Field}_default`;
	const currentDefaultSocValue = (getIn(values, defaultSocInputName) as number | null | undefined) ?? null;

	useEffect(() => {
		// Keep the derived default value in sync across Formik reinitializations.
		// Edit Activity uses enableReinitialize, which can reset this field.
		if (currentDefaultSocValue !== defaultSocT2) {
			setFieldValue(defaultSocInputName, defaultSocT2, false);
		}
	}, [currentDefaultSocValue, defaultSocInputName, defaultSocT2, setFieldValue]);

	const defaultSocInput = useMemo<ClassicInputGroup>(
		() => ({
			type: FieldType.CLASSIC,
			inputName: defaultSocInputName,
			inputType: "number",
			label: "module.socref",
			unit: "[tC/ha]",
			textend: true,
			disabled: true,
		}),
		[defaultSocInputName],
	);

	const socT2Input = useMemo<ClassicInputGroup>(
		() => ({
			type: FieldType.CLASSIC,
			inputName: socT2Field,
			inputType: "number",
			label: "module.socref_tier_two",
			unit: "[tC/ha]",
			textend: true,
			disabled,
		}),
		[socT2Field, disabled],
	);

	return (
		<>
			<div className="py-1">
				<GenericInputGroup inputGroup={defaultSocInput} />
			</div>
			<div className="py-1">
				<GenericInputGroup inputGroup={socT2Input} />
			</div>
		</>
	);
};

export default ActivityDefaultSocSubmodule;
